import { ArrowDownward, ArrowUpward, CheckBox, CheckBoxOutlineBlank, Close, ShowChart } from "@mui/icons-material"
import { Box, Collapse, Divider, useTheme } from "@mui/material"
import { useEffect, useMemo, useRef, useState } from "react"
import { intl } from "src/internationalization/intl"
import { formatCurrencyBRL } from "src/lib/transformers/ClientsOverviewInfo"
import type {
    DailyExecutionPoint,
    MovementPortfolioRollup,
    ObjectiveMovementSummary,
    OpportunityImpactedClient,
} from "src/services/api/opportunities/types"
import {
    normalizeAdvisorDisplayName,
    normalizeOpportunityAccountId,
    normalizePortfolioClientNameKey,
} from "src/services/api/opportunities/accountNormalize"
import type { Filter } from "../../basics/TabFilters/types"
import { BoxCard, Button, Grid, IconButton, TabFilters, Typography } from "../../basics"
import { getMonitoringTableContainerSx, getMonitoringTableSx } from "./monitoringTableStyles"
import {
    AdvisorExecutionChart,
    barColorForRatio,
    countClientsDone,
    displayRatioForAdvisor,
    formatClientsDone,
    formatPercent,
    type AdvisorChartPortfolio,
    type MonitoringMetricMode,
} from "./AdvisorExecutionChart"
import { AdvisorExecutionTable, AdvisorPortfolioDrilldown } from "./MonitoringDrilldown"
import { ExecutionOverTimeChart } from "./ExecutionOverTimeChart"

type MonitoringViewMode = "timeline" | "chart" | "table"

export interface ObjectiveMonitoringPanelProps {
    movementPortfolioRollups?: MovementPortfolioRollup[]
    movementSummary?: ObjectiveMovementSummary
    objectiveWarnings?: string[]
    impactedClients?: OpportunityImpactedClient[]
    dailyExecutionSeries?: DailyExecutionPoint[]
    createdDate?: string
    dueDate?: string
    /** inline = colapsável com botão; standalone = painel fixo abaixo da listagem. */
    variant?: "inline" | "standalone"
    /** Título da oportunidade (standalone). */
    objectiveTitle?: string
    /** Fechar painel standalone. */
    onClose?: () => void
    /** @deprecated Use variant="inline" */
    embedded?: boolean
    open?: boolean
    onOpenChange?: (open: boolean) => void
}

export interface ObjectiveMonitorTriggerProps {
    active?: boolean
    onClick: () => void
}

const ObjectiveMonitorTrigger = ({ active = false, onClick }: ObjectiveMonitorTriggerProps) => (
    <Button
        variant="outlined"
        color="primary"
        size="small"
        fullWidth
        role="checkbox"
        aria-checked={active}
        startIcon={
            active ? (
                <CheckBox sx={{ fontSize: 18 }} />
            ) : (
                <CheckBoxOutlineBlank sx={{ fontSize: 18 }} />
            )
        }
        onClick={onClick}
        sx={{
            minHeight: 32,
            fontSize: "0.8125rem",
            fontWeight: 600,
            textTransform: "none",
            // Checkbox: todos os cards continuam igualmente selecionáveis;
            // o marcado ganha apenas um leve preenchimento.
            bgcolor: active ? "rgba(162, 205, 237, 0.12)" : "transparent",
            "& .MuiButton-startIcon": { mr: 0.5 },
        }}
    >
        {intl("objectiveMonitorAction")}
    </Button>
)

type SortDirection = "desc" | "asc"

const UNASSIGNED_ADVISOR_KEY = "__unassigned__"

const normalizeAdvisorId = (raw: unknown): string | undefined => {
    if (raw === undefined || raw === null) return undefined
    const s = String(raw).trim()
    return s || undefined
}

const advisorGroupKey = (row: MovementPortfolioRollup): string => {
    const ia = row.investment_advisor
    if (!ia) return UNASSIGNED_ADVISOR_KEY
    const id = normalizeAdvisorId(ia.id) ?? normalizeAdvisorId(ia.element_id)
    if (id) return `id:${id}`
    const n = normalizeAdvisorDisplayName(ia.name)
    if (n) return `name:${n.toLocaleUpperCase("pt-BR")}`
    return UNASSIGNED_ADVISOR_KEY
}

type AdvisorAggRow = {
    groupKey: string
    advisorLabel: string
    opportunitySum: number
    liquidoSum: number
    percentRatio: number | null
    clientsDone: number
    clientsTotal: number
    portfolios: AdvisorChartPortfolio[]
}

/** Rótulo "Nome (conta)" de uma carteira para o detalhamento do gráfico. */
const portfolioDisplayLabel = (row: MovementPortfolioRollup): string => {
    const account = row.portfolio_account ?? row.account ?? "—"
    return row.portfolio_name && String(account) !== "—"
        ? `${row.portfolio_name} (${String(account)})`
        : String(account)
}

const advisorPieceLabel = (row: MovementPortfolioRollup): string => {
    const ia = row.investment_advisor
    const name = normalizeAdvisorDisplayName(ia?.name)
    if (name) return name
    const id = normalizeAdvisorId(ia?.id)
    if (id) return id
    return ""
}

const aggregateByAdvisor = (rows: EnrichedRollup[]): AdvisorAggRow[] => {
    const m = new Map<string, { label: string; opp: number; liq: number; portfolios: AdvisorChartPortfolio[] }>()
    for (const er of rows) {
        const gk = advisorGroupKey(er.row)
        const opp =
            er.opportunityVolume != null && Number.isFinite(er.opportunityVolume)
                ? er.opportunityVolume
                : 0
        const net = Number(er.row.total_amount_net)
        const liq = Number.isFinite(net) ? net : 0
        const pieceLabel = advisorPieceLabel(er.row)
        const portfolio: AdvisorChartPortfolio = {
            label: portfolioDisplayLabel(er.row),
            account: er.accountKey,
            opportunityVolume: er.opportunityVolume,
            executedValue: liq,
            percentRatio: er.percentRatio,
        }
        const prev = m.get(gk)
        if (!prev) {
            m.set(gk, { label: pieceLabel, opp, liq, portfolios: [portfolio] })
        } else {
            const label = prev.label || pieceLabel
            m.set(gk, {
                label,
                opp: prev.opp + opp,
                liq: prev.liq + liq,
                portfolios: [...prev.portfolios, portfolio],
            })
        }
    }
    return Array.from(m.entries()).map(([groupKey, v]) => {
        const { clientsDone, clientsTotal } = countClientsDone(v.portfolios)
        return {
            groupKey,
            advisorLabel: v.label,
            opportunitySum: v.opp,
            liquidoSum: v.liq,
            percentRatio: v.opp > 0 ? v.liq / v.opp : null,
            clientsDone,
            clientsTotal,
            portfolios: v.portfolios,
        }
    })
}

const sortAdvisorRows = (
    rows: AdvisorAggRow[],
    direction: SortDirection,
    metricMode: MonitoringMetricMode,
): AdvisorAggRow[] => {
    const copy = [...rows]
    copy.sort((a, b) => {
        const ra = displayRatioForAdvisor(a, metricMode)
        const rb = displayRatioForAdvisor(b, metricMode)
        const na = ra === null
        const nb = rb === null
        if (na && nb) return a.advisorLabel.localeCompare(b.advisorLabel, "pt-BR")
        if (na) return 1
        if (nb) return -1
        const cmp = ra! - rb!
        return direction === "desc" ? -cmp : cmp
    })
    return copy
}

const volumeByImpactedAccount = (impacted: OpportunityImpactedClient[] | undefined): Map<string, number> => {
    const m = new Map<string, number>()
    if (!impacted?.length) return m
    for (const ic of impacted) {
        const key = normalizeOpportunityAccountId(ic?.account ?? ic?.p?.account)
        if (!key) continue
        const vol = Number(ic.volumeOportunidade)
        if (!Number.isFinite(vol)) continue
        m.set(key, vol)
    }
    return m
}

/** When exactly one impacted row uses this display name, map name → volume (account mismatch fallback). */
const volumeByImpactedUniqueName = (impacted: OpportunityImpactedClient[] | undefined): Map<string, number> => {
    const m = new Map<string, number>()
    if (!impacted?.length) return m
    const counts = new Map<string, number>()
    for (const ic of impacted) {
        const nk = normalizePortfolioClientNameKey(ic?.name ?? ic?.p?.name)
        if (!nk) continue
        counts.set(nk, (counts.get(nk) ?? 0) + 1)
    }
    for (const ic of impacted) {
        const nk = normalizePortfolioClientNameKey(ic?.name ?? ic?.p?.name)
        if (!nk || counts.get(nk) !== 1) continue
        const vol = Number(ic.volumeOportunidade)
        if (!Number.isFinite(vol)) continue
        m.set(nk, vol)
    }
    return m
}

const rollupAccountKey = (row: MovementPortfolioRollup): string =>
    normalizeOpportunityAccountId(row.portfolio_account ?? row.account)

type EnrichedRollup = {
    row: MovementPortfolioRollup
    accountKey: string
    opportunityVolume: number | null
    percentRatio: number | null
    /** Carteira só em Ver Clientes — sem linha no rollup de movimentos */
    impactedOnly?: boolean
}

const enrichRollups = (
    rollups: MovementPortfolioRollup[] | undefined,
    impacted: OpportunityImpactedClient[] | undefined
): EnrichedRollup[] => {
    const oppMap = volumeByImpactedAccount(impacted)
    const nameMap = volumeByImpactedUniqueName(impacted)
    const list = rollups ?? []
    return list.map((row) => {
        const accountKey = rollupAccountKey(row)
        let oppVol = accountKey ? oppMap.get(accountKey) : undefined
        let matched = oppVol !== undefined && Number.isFinite(oppVol)

        if (!matched && row.portfolio_name) {
            const nk = normalizePortfolioClientNameKey(row.portfolio_name)
            const byName = nk ? nameMap.get(nk) : undefined
            if (byName !== undefined && Number.isFinite(byName)) {
                oppVol = byName
                matched = true
            }
        }

        const liquido = Number(row.total_amount_net)

        let percentRatio: number | null = null
        if (matched && oppVol! > 0 && Number.isFinite(liquido)) {
            percentRatio = liquido / oppVol!
        }

        return {
            row,
            accountKey,
            opportunityVolume: matched ? oppVol! : null,
            percentRatio,
        }
    })
}

/** Impacted clients (Ver Clientes) whose account never appears on a movement rollup row — líquido 0; assessor N/A. Valor oportunidade = soma Ver Clientes para a conta. */
const enrichImpactedWithoutRollup = (
    impacted: OpportunityImpactedClient[] | undefined,
    rollups: MovementPortfolioRollup[] | undefined
): EnrichedRollup[] => {
    const rollupKeys = new Set<string>()
    for (const r of rollups ?? []) {
        const k = rollupAccountKey(r)
        if (k) rollupKeys.add(k)
    }

    type ImpactedAgg = {
        volumeSum: number
        labelName: string
        advisorNames: Set<string>
        advisorIds: Set<string>
        advisorElementIds: Set<string>
    }

    const agg = new Map<string, ImpactedAgg>()
    for (const ic of impacted ?? []) {
        const k = normalizeOpportunityAccountId(ic?.account ?? ic?.p?.account)
        if (!k || rollupKeys.has(k)) continue
        const vol = Number(ic.volumeOportunidade)
        const name = String(ic?.name ?? ic?.p?.name ?? "").trim() || "—"
        const addVol = Number.isFinite(vol) ? vol : 0
        const adv = normalizeAdvisorDisplayName(ic?.advisor_name)
        const advId = normalizeAdvisorId(ic?.advisor_id)
        const advElementId = normalizeAdvisorId(ic?.advisor_element_id)
        const prev = agg.get(k)
        if (prev) {
            const advisorNames = new Set(prev.advisorNames)
            const advisorIds = new Set(prev.advisorIds)
            const advisorElementIds = new Set(prev.advisorElementIds)
            if (adv) advisorNames.add(adv)
            if (advId) advisorIds.add(advId)
            if (advElementId) advisorElementIds.add(advElementId)
            agg.set(k, {
                volumeSum: prev.volumeSum + addVol,
                labelName: prev.labelName,
                advisorNames,
                advisorIds,
                advisorElementIds,
            })
        } else {
            const advisorNames = new Set<string>()
            const advisorIds = new Set<string>()
            const advisorElementIds = new Set<string>()
            if (adv) advisorNames.add(adv)
            if (advId) advisorIds.add(advId)
            if (advElementId) advisorElementIds.add(advElementId)
            agg.set(k, {
                volumeSum: addVol,
                labelName: name,
                advisorNames,
                advisorIds,
                advisorElementIds,
            })
        }
    }

    const out: EnrichedRollup[] = []
    for (const [
        accountKey,
        { volumeSum, labelName, advisorNames, advisorIds, advisorElementIds },
    ] of Array.from(agg.entries())) {
        let percentRatio: number | null = null
        if (volumeSum > 0) {
            percentRatio = 0
        }
        const advisorsSorted = Array.from(advisorNames).sort((a, b) => a.localeCompare(b, "pt-BR"))
        const advisorLabel =
            advisorsSorted.length === 0
                ? undefined
                : advisorsSorted.length === 1
                  ? advisorsSorted[0]
                  : advisorsSorted.join(" · ")
        const singleAdvisorId = advisorIds.size === 1 ? Array.from(advisorIds)[0] : undefined
        const singleAdvisorElementId =
            advisorElementIds.size === 1 ? Array.from(advisorElementIds)[0] : undefined
        const canonicalAdvisorId = singleAdvisorId ?? singleAdvisorElementId
        out.push({
            impactedOnly: true,
            accountKey,
            opportunityVolume: volumeSum,
            percentRatio,
            row: {
                portfolio_account: accountKey,
                account: accountKey,
                total_amount_net: 0,
                portfolio_name: labelName,
                investment_advisor: advisorLabel
                    ? {
                          id: canonicalAdvisorId,
                          element_id: singleAdvisorElementId,
                          name: advisorLabel,
                      }
                    : null,
            },
        })
    }
    return out
}

const ObjectiveMonitoringPanel = ({
    movementPortfolioRollups,
    movementSummary,
    objectiveWarnings,
    impactedClients,
    dailyExecutionSeries,
    createdDate,
    dueDate,
    variant = "inline",
    objectiveTitle,
    onClose,
    embedded = false,
    open: openControlled,
    onOpenChange,
}: ObjectiveMonitoringPanelProps) => {
    const resolvedVariant = variant === "standalone" ? "standalone" : "inline"
    const [openInternal, setOpenInternal] = useState(false)
    const open = resolvedVariant === "standalone" ? true : (openControlled ?? openInternal)
    const setOpen = (next: boolean | ((prev: boolean) => boolean)) => {
        const value = typeof next === "function" ? next(open) : next
        if (onOpenChange) onOpenChange(value)
        else setOpenInternal(value)
    }
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc")
    const [viewMode, setViewMode] = useState<MonitoringViewMode>("chart")
    const [metricMode, setMetricMode] = useState<MonitoringMetricMode>("volume")
    const [selectedAdvisors, setSelectedAdvisors] = useState<string[]>([])
    const didInitSelection = useRef(false)

    const summaryTotal =
        movementSummary && typeof movementSummary.total_amount_net === "number"
            ? movementSummary.total_amount_net
            : movementSummary && typeof movementSummary.grand_total_amount_net === "number"
              ? movementSummary.grand_total_amount_net
              : undefined

    const mergedFlat = useMemo(() => {
        const enriched = enrichRollups(movementPortfolioRollups, impactedClients)
        const impactedOnly = enrichImpactedWithoutRollup(impactedClients, movementPortfolioRollups)
        return [...enriched, ...impactedOnly]
    }, [movementPortfolioRollups, impactedClients])

    const advisorAggregated = useMemo(() => aggregateByAdvisor(mergedFlat), [mergedFlat])

    const sortedAdvisorRows = useMemo(
        () => sortAdvisorRows(advisorAggregated, sortDirection, metricMode),
        [advisorAggregated, sortDirection, metricMode]
    )

    // Seleciona o assessor de maior execução na primeira carga (drill-down já populado).
    useEffect(() => {
        if (didInitSelection.current || sortedAdvisorRows.length === 0) return
        didInitSelection.current = true
        setSelectedAdvisors([sortedAdvisorRows[0].groupKey])
    }, [sortedAdvisorRows])

    // Total potential for the objective (sum across all advisors) — the reference the Resumo total is a fraction of.
    const totalOpportunitySum = useMemo(
        () => sortedAdvisorRows.reduce((sum, r) => sum + r.opportunitySum, 0),
        [sortedAdvisorRows]
    )
    const summaryRatio =
        typeof summaryTotal === "number" && totalOpportunitySum > 0
            ? summaryTotal / totalOpportunitySum
            : null

    const toggleAdvisor = (groupKey: string) => {
        setSelectedAdvisors((prev) =>
            prev.includes(groupKey) ? prev.filter((k) => k !== groupKey) : [...prev, groupKey]
        )
    }

    const selectedRows = useMemo(
        () => sortedAdvisorRows.filter((r) => selectedAdvisors.includes(r.groupKey)),
        [sortedAdvisorRows, selectedAdvisors]
    )

    // Combined subtotal for the selected advisors (independent of the per-portfolio breakdown below).
    const selectedSummary = useMemo(() => {
        if (selectedRows.length === 0) return null
        const opportunitySum = selectedRows.reduce((sum, r) => sum + r.opportunitySum, 0)
        const liquidoSum = selectedRows.reduce((sum, r) => sum + r.liquidoSum, 0)
        const clientsDone = selectedRows.reduce((sum, r) => sum + r.clientsDone, 0)
        const clientsTotal = selectedRows.reduce((sum, r) => sum + r.clientsTotal, 0)
        const percentRatio = opportunitySum > 0 ? liquidoSum / opportunitySum : null
        return { opportunitySum, liquidoSum, clientsDone, clientsTotal, percentRatio }
    }, [selectedRows])

    const selectedSummaryRatio = selectedSummary ? displayRatioForAdvisor(selectedSummary, metricMode) : null

    const showMonitoringTable = mergedFlat.length > 0
    const theme = useTheme()
    const tableSx = useMemo(() => getMonitoringTableSx(theme), [theme])
    const tableContainerSx = useMemo(() => getMonitoringTableContainerSx(theme), [theme])

    const viewFilters = useMemo<Filter[]>(
        () => [
            { id: "timeline", label: intl("objectiveMonitoringViewTimeline"), value: "timeline" },
            { id: "chart", label: intl("objectiveMonitoringViewChart"), value: "chart" },
            { id: "table", label: intl("objectiveMonitoringViewTable"), value: "table" },
        ],
        []
    )
    const selectedViewFilter = viewFilters.find((f) => f.id === viewMode) ?? viewFilters[0]

    const metricFilters = useMemo<Filter[]>(
        () => [
            { id: "volume", label: intl("objectiveMonitoringMetricVolume"), value: "volume" },
            { id: "clients", label: intl("objectiveMonitoringMetricClients"), value: "clients" },
        ],
        []
    )
    const selectedMetricFilter = metricFilters.find((f) => f.id === metricMode) ?? metricFilters[0]

    const panelBody = (
        <BoxCard sx={{ mt: resolvedVariant === "standalone" ? 0 : 1, p: 2 }}>
            {resolvedVariant === "standalone" && (
                <Grid container justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Grid item sx={{ pr: 1, flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="secondary">
                            {intl("objectiveMonitoringBadge")}
                        </Typography>
                        <Typography variant="h3" sx={{ mt: 0.25, lineHeight: 1.25 }}>
                            {objectiveTitle || "—"}
                        </Typography>
                    </Grid>
                    {onClose ? (
                        <IconButton
                            size="small"
                            color="neutral"
                            variant="clean"
                            aria-label={intl("objectiveMonitorCollapse")}
                            onClick={onClose}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    ) : null}
                </Grid>
            )}
                    {typeof summaryTotal === "number" && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="secondary">
                                {intl("objectiveMonitoringSummary")}
                            </Typography>
                            <Box sx={{ mt: 0.25 }}>
                                <Typography component="span" variant="h3">
                                    {formatCurrencyBRL(summaryTotal)}
                                </Typography>
                                {totalOpportunitySum > 0 && (
                                    <>
                                        <Typography component="span" variant="h3">
                                            {" "}
                                            {intl("objectiveMonitoringSummaryOfTotal", {
                                                values: { total: formatCurrencyBRL(totalOpportunitySum) },
                                            })}
                                            {" · "}
                                        </Typography>
                                        <Typography component="span" variant="h3" color="quaternary">
                                            {formatPercent(summaryRatio)}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                            {typeof movementSummary?.portfolio_count === "number" &&
                                typeof movementSummary?.movement_count === "number" && (
                                    <Typography variant="caption" color="secondary" sx={{ display: "block", mt: 0.5 }}>
                                        {intl("objectiveMonitoringMeta", {
                                            values: {
                                                portfolioCount: String(movementSummary.portfolio_count),
                                                movementCount: String(movementSummary.movement_count),
                                            },
                                        })}
                                    </Typography>
                                )}
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                            gap: 2,
                            mb: 1.5,
                        }}
                    >
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 1,
                                }}
                            >
                                <TabFilters
                                    filters={viewFilters}
                                    selectedFilter={selectedViewFilter}
                                    onFilterChange={(f) => setViewMode(f.id as MonitoringViewMode)}
                                />
                                {viewMode !== "timeline" && showMonitoringTable ? (
                                    <TabFilters
                                        filters={metricFilters}
                                        selectedFilter={selectedMetricFilter}
                                        onFilterChange={(f) => setMetricMode(f.id as MonitoringMetricMode)}
                                    />
                                ) : null}
                            </Box>
                            {viewMode !== "timeline" && showMonitoringTable ? (
                                <Grid container alignItems="center" justifyContent="flex-end" gap={1} flexWrap="wrap">
                                    <Typography variant="caption" color="secondary">
                                        {sortDirection === "desc"
                                            ? intl("objectiveMonitoringSortHintDesc")
                                            : intl("objectiveMonitoringSortHintAsc")}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        color="quaternary"
                                        variant="clean"
                                        aria-label={intl("objectiveMonitoringSortToggleAria")}
                                        onClick={() =>
                                            setSortDirection((d) => (d === "desc" ? "asc" : "desc"))
                                        }
                                    >
                                        {sortDirection === "desc" ? (
                                            <ArrowDownward fontSize="small" />
                                        ) : (
                                            <ArrowUpward fontSize="small" />
                                        )}
                                    </IconButton>
                                </Grid>
                            ) : null}
                        </Box>
                    </Box>

                    {viewMode === "timeline" ? (
                        createdDate ? (
                            <ExecutionOverTimeChart
                                createdDate={createdDate}
                                dueDate={dueDate}
                                series={dailyExecutionSeries}
                            />
                        ) : (
                            <Typography variant="caption" color="secondary">
                                {intl("objectiveMonitoringTimelineEmpty")}
                            </Typography>
                        )
                    ) : showMonitoringTable ? (
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                gap: 2,
                                alignItems: "start",
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "neutral.lighter", fontWeight: 700, display: "block" }}
                                >
                                    {intl("objectiveMonitoringByAdvisorTitle")}
                                </Typography>
                                {viewMode === "chart" ? (
                                    <AdvisorExecutionChart
                                        rows={sortedAdvisorRows}
                                        selectedKeys={selectedAdvisors}
                                        onToggle={toggleAdvisor}
                                        metricMode={metricMode}
                                    />
                                ) : (
                                    <AdvisorExecutionTable
                                        rows={sortedAdvisorRows}
                                        selectedKeys={selectedAdvisors}
                                        onToggle={toggleAdvisor}
                                        metricMode={metricMode}
                                        tableSx={tableSx}
                                        tableContainerSx={tableContainerSx}
                                    />
                                )}
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                                {selectedSummary && (
                                    <>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 1,
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: "neutral.lighter", fontWeight: 700, display: "block" }}
                                                    noWrap
                                                >
                                                    {intl("objectiveMonitoringSelectedSummaryLabel", {
                                                        values: { count: String(selectedRows.length) },
                                                    })}
                                                </Typography>
                                                <Typography variant="caption" color="quaternary" noWrap>
                                                    {metricMode === "clients"
                                                        ? formatClientsDone(
                                                              selectedSummary.clientsDone,
                                                              selectedSummary.clientsTotal
                                                          )
                                                        : `${formatCurrencyBRL(selectedSummary.liquidoSum)} / ${formatCurrencyBRL(selectedSummary.opportunitySum)}`}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: barColorForRatio(selectedSummaryRatio),
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {formatPercent(selectedSummaryRatio)}
                                            </Typography>
                                        </Box>
                                        <Divider sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.08)" }} />
                                    </>
                                )}
                                {selectedRows.length > 0 ? (
                                    <AdvisorPortfolioDrilldown
                                        advisors={selectedRows}
                                        viewMode={viewMode}
                                        metricMode={metricMode}
                                        sortDirection={sortDirection}
                                        tableSx={tableSx}
                                        tableContainerSx={tableContainerSx}
                                    />
                                ) : (
                                    <Typography variant="caption" color="secondary">
                                        {intl("objectiveMonitoringDrilldownEmpty")}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="caption" color="secondary">
                            {intl("objectiveMonitoringEmpty")}
                        </Typography>
                    )}

                    {objectiveWarnings && objectiveWarnings.length > 0 && (
                        <Typography
                            variant="caption"
                            sx={{ mt: 1.5, display: "block", color: "warning.main" }}
                        >
                            {objectiveWarnings.join(" · ")}
                        </Typography>
                    )}
        </BoxCard>
    )

    if (resolvedVariant === "standalone") {
        return <Box sx={{ width: "100%" }}>{panelBody}</Box>
    }

    return (
        <Box sx={{ width: "100%", mt: embedded ? 0 : 0.5 }}>
            <Button
                variant="text"
                color="primary"
                size="small"
                startIcon={<ShowChart sx={{ fontSize: 18 }} />}
                onClick={() => setOpen(!open)}
                sx={{
                    minHeight: 28,
                    px: embedded ? 0 : 0.5,
                    py: 0,
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    textTransform: "none",
                    justifyContent: "flex-start",
                    "& .MuiButton-startIcon": { mr: 0.5 },
                }}
            >
                {open ? intl("objectiveMonitorCollapse") : intl("objectiveMonitorAction")}
            </Button>

            <Collapse in={open} timeout="auto" unmountOnExit>
                {panelBody}
            </Collapse>
        </Box>
    )
}

export { ObjectiveMonitoringPanel, ObjectiveMonitorTrigger }
